import { Component, OnInit, OnDestroy, inject, HostListener } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { trigger, state, style, transition, animate } from '@angular/animations';
import {
  RdsButtonModule,
  RdsFormFieldModule,
  RdsDatepickerModule,
  RdsProgressSpinnerModule,
  RdsSwitchModule,
  RdsIconsModule,
  RDS_DATE_FORMATS,
  RdsTooltipModule,
} from '@rds/angular-components';
import { FilterService } from '../services/filter.service';
import { EntityTypeFilter, Filter } from '../models/filter';
import { StrategicPillar, StrategicPillarService } from '../services/strategic-pillar.service';
import { UserAudienceResponse, AudienceService } from '../services/audience.service';
import { TimelineDataService } from '../services/timeline-data.service';
import { FeatureService } from '../services/feature.service';
import { Study } from '../models/metadata';
import { DateFormatter } from '../models/date-formatter.model';
import {
  DynamicFilterAutocompletionComponent,
} from '../filter-section/dynamic-filter-autocompletion/dynamic-filter-autocompletion.component';
import { FilterPanelService } from '../services/filter-panel.service';

type FilterForm = FormGroup<{
  timespan: FormGroup<{
    start: FormControl<Date>;
    end: FormControl<Date>;
  }>;
  studies: FormControl<Array<Study> | null>;
  persons: FormControl<Array<string> | null>;
  targets: FormControl<Array<string> | null>;
  strategicPillars: FormControl<Array<StrategicPillar> | null>;
  audiences: FormControl<Array<UserAudienceResponse> | null>;
}>;

@Component({
  selector: 'app-filter-drawer',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RdsButtonModule,
    RdsFormFieldModule,
    RdsDatepickerModule,
    RdsProgressSpinnerModule,
    RdsSwitchModule,
    RdsIconsModule,
    RdsTooltipModule,
    DynamicFilterAutocompletionComponent,
  ],
  providers: [{ provide: RDS_DATE_FORMATS, useValue: DateFormatter.getDatePickerFormat() }],
  templateUrl: './filter-drawer.component.html',
  styleUrls: ['./filter-drawer.component.scss'],
  animations: [
    trigger('slidePanel', [
      transition(':enter', [
        style({ transform: 'translateX(-100%)' }),
        animate('300ms cubic-bezier(0.25, 0.8, 0.25, 1)', style({ transform: 'translateX(0)' })),
      ]),
      transition(':leave', [
        animate('200ms cubic-bezier(0.25, 0.8, 0.25, 1)', style({ transform: 'translateX(-100%)' })),
      ]),
    ]),
    trigger('fadeBackdrop', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('300ms ease', style({ opacity: 1 })),
      ]),
      transition(':leave', [
        animate('200ms ease', style({ opacity: 0 })),
      ]),
    ]),
  ],
})
export class FilterDrawerComponent implements OnInit, OnDestroy {
  panelOpen = false;

  filters!: Filter;

  filterForm!: FilterForm;
  loading = false;
  formBuilder = inject(FormBuilder);

  // Entity type toggle states — enabled by default
  entityToggles: { id: EntityTypeFilter; label: string; checked: boolean }[] = [
    { id: 'publications', label: 'Publications', checked: true },
    { id: 'activities', label: 'Activities', checked: true },
    { id: 'vvpmActivities', label: 'Materials', checked: true },
    { id: 'congresses', label: 'Congresses', checked: true },
    { id: 'milestones', label: 'Milestones', checked: true },
  ];

  allPillars: StrategicPillar[] = [];
  allAudiences: UserAudienceResponse[] = [];

  private subscriptions: Subscription[] = [];

  constructor(
    private filterService: FilterService,
    private strategicPillarService: StrategicPillarService,
    private audienceService: AudienceService,
    private timelineDataService: TimelineDataService,
    private filterPanelService: FilterPanelService,
    public features: FeatureService,
  ) {
    this.entityToggles = this.entityToggles.filter(
      toggle => this.features.isEntityTypeEnabled(toggle.id)
    );
  }

  ngOnInit() {
    const panelSub = this.filterPanelService.open$.subscribe((open) => {
      this.panelOpen = open;
    });
    this.subscriptions.push(panelSub);

    const filterSub = this.filterService.filterData.subscribe((filter) => {
      this.filters = filter;
      this.updateEntityToggles(filter);
      this.setupForm();
    });
    this.subscriptions.push(filterSub);

    this.loadStrategicPillars();
    this.loadAudiences();

    const pillarRefreshSub = this.strategicPillarService.refresh$.subscribe(() => {
      this.loadStrategicPillars();
    });
    this.subscriptions.push(pillarRefreshSub);

    const audienceRefreshSub = this.audienceService.refresh$.subscribe(() => {
      this.loadAudiences();
    });
    this.subscriptions.push(audienceRefreshSub);

    const timelineSub = this.timelineDataService.timelineData.subscribe(() => {
      this.loading = false;
    });
    this.subscriptions.push(timelineSub);
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  togglePanel() {
    this.filterPanelService.toggle();
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    if (this.panelOpen) {
      this.filterPanelService.close();
    }
  }

  // --- Entity type toggles ---

  onEntityToggle(entityType: EntityTypeFilter) {
    this.filterService.toggleEntityType(entityType);
  }

  private updateEntityToggles(filter: Filter) {
    this.entityToggles.forEach((toggle) => {
      toggle.checked = filter.entityTypes.includes(toggle.id);
    });
  }

  // --- Form ---

  setupForm() {
    this.filterForm = this.formBuilder.group({
      timespan: this.formBuilder.group({
        start: new FormControl(this.filters.timespan.start, {
          nonNullable: true,
          validators: [Validators.required],
        }),
        end: new FormControl(this.filters.timespan.end, {
          nonNullable: true,
          validators: [Validators.required],
        }),
      }),
      studies: [this.filters.studies],
      persons: [this.filters.persons],
      targets: [this.filters.targets],
      strategicPillars: [this.filters.strategicPillars],
      audiences: [this.filters.audiences],
    });
  }

  inTimespanBounds = (d: Date | null): boolean => {
    return !!d && d >= Filter.DEFAULT_TIMESPAN_START && d <= Filter.DEFAULT_TIMESPAN_END;
  };

  blockEnter(e: InputEvent) {
    if (e.inputType === 'insertLineBreak') {
      e.preventDefault();
    }
  }

  applyFilters() {
    this.loading = true;
    this.filterService.updateFilters({
      ...this.filterForm.value,
      global: this.filters.global,
      entityTypes: this.filters.entityTypes,
    } as Filter);
  }

  clearFilters() {
    this.filterService.clearFilters(false);
  }

  // --- Data loading ---

  private loadStrategicPillars() {
    this.strategicPillarService.getAllPillars().subscribe((pillars) => {
      this.allPillars = pillars;
    });
  }

  private loadAudiences() {
    this.audienceService.getInUseAudiences().subscribe((response) => {
      this.allAudiences = response.map((item) => ({
        id: item.id,
        title: item.name,
        description: '',
        color_code: '#6366F1',
        audience_type: 'audience',
        veeva_audience_id: item.id,
        veeva_specialist_id: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }));
    });
  }
}
